import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, HeartOff } from "lucide-react";
import { getFavorites, removeFavorite } from "@/lib/favoriteQuotes";

// "Lines you kept" — a quiet on-device list of quotes that helped before.
// Modeled after the About overlay so the design language stays consistent.
// by David Hunt
export const FavoriteQuotes = ({ open, onClose }) => {
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    if (open) setFavorites(getFavorites());
  }, [open]);

  const handleRemove = (text) => {
    removeFavorite(text);
    setFavorites((prev) => prev.filter((q) => q.text !== text));
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="favorites-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1.0, ease: "easeInOut" }}
        className="fixed inset-0 z-30 overflow-y-auto overscroll-contain"
        data-testid="favorites-overlay"
      >
        <div
          className="fixed inset-0 bg-black/45 backdrop-blur-2xl pointer-events-none"
          aria-hidden="true"
        />

        <button
          onClick={onClose}
          className="fixed w-10 h-10 rounded-full flex items-center justify-center bg-white/5 border border-white/10 backdrop-blur-xl text-white/70 hover:bg-white/10 hover:text-white transition-colors duration-500 z-10"
          style={{
            top: "calc(env(safe-area-inset-top) + 1rem)",
            right: "calc(env(safe-area-inset-right) + 1rem)",
          }}
          data-testid="favorites-close-btn"
          aria-label="Close your kept lines"
        >
          <X size={18} strokeWidth={1.5} />
        </button>

        <div className="relative min-h-full flex flex-col items-center px-6 py-20 sm:py-24">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeInOut", delay: 0.2 }}
            className="w-full max-w-xl"
          >
            <h2 className="font-heading italic text-white/90 text-2xl sm:text-3xl text-center">
              lines you kept
            </h2>
            <p className="mt-2 text-center text-xs text-white/40 tracking-wider uppercase">
              saved only on this device
            </p>

            <div className="mt-10 space-y-4">
              {favorites.length === 0 && (
                <p
                  className="text-center text-white/50 text-sm leading-relaxed"
                  data-testid="favorites-empty"
                >
                  nothing kept yet — tap the heart under a line that lands, and
                  it will wait for you here.
                </p>
              )}

              <AnimatePresence initial={false}>
                {favorites.map((q) => (
                  <motion.div
                    key={q.text}
                    layout
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.8, ease: "easeInOut" }}
                    className="relative rounded-2xl bg-white/5 border border-white/10 backdrop-blur-xl px-5 py-4 pr-12"
                    data-testid="favorite-quote-item"
                  >
                    <p className="font-heading italic text-white/85 text-lg leading-relaxed">
                      {q.text}
                    </p>
                    {q.author && (
                      <p className="mt-1 text-sm text-white/50">— {q.author}</p>
                    )}
                    <button
                      onClick={() => handleRemove(q.text)}
                      className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors duration-500"
                      data-testid="favorite-remove-btn"
                      aria-label="Let this line go"
                      title="let this one go"
                    >
                      <HeartOff size={14} strokeWidth={1.5} />
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FavoriteQuotes;
